import { getBridgeRoot } from '../../../files/cwd/bridge-root.js';
import { getCwdHashForFileIndex } from '../../../files/index/paths.js';
import { normalizeResolvedPath } from '../../../paths/normalize-resolved-path.js';
import type { Database } from 'node-sqlite3-wasm';
import type { SymbolIndexParentContext } from '../storage/parent-context.js';
import { withCodeNavCacheSqliteSync } from '../../../sqlite/code-nav-cache/database.js';
import { SYMBOL_INDEX_VERSION } from './symbol-index-version.js';

export type SymbolIndexParentRow = SymbolIndexParentContext & {
  path: string;
};

function normalizeParentPath(resolved: string): string {
  return normalizeResolvedPath(resolved);
}

function resolveParentKind(resolved: string): 'base' | 'overlay' {
  return normalizeParentPath(resolved) === normalizeParentPath(getBridgeRoot()) ? 'base' : 'overlay';
}

function ensureBaseParentId(db: Database): number {
  const bridgeRoot = normalizeParentPath(getBridgeRoot());
  const existing = db.get('SELECT id FROM symbol_index_parent_path WHERE path = ?', [bridgeRoot]) as
    | { id: number | bigint }
    | null;
  if (existing != null) return Number(existing.id);
  return upsertSymbolIndexParentPath(db, bridgeRoot);
}

export function upsertSymbolIndexParentPath(db: Database, resolved: string): number {
  const normalized = normalizeParentPath(resolved);
  const now = new Date().toISOString();
  const kind = resolveParentKind(normalized);
  const baseParentId = kind === 'overlay' ? ensureBaseParentId(db) : null;

  db.run(
    `INSERT INTO symbol_index_parent_path (path, path_hash, updated_at, index_version, kind, base_parent_id)
     VALUES (?, ?, ?, ?, ?, ?)
     ON CONFLICT(path) DO UPDATE SET
       path_hash = excluded.path_hash,
       updated_at = excluded.updated_at,
       index_version = excluded.index_version,
       kind = excluded.kind,
       base_parent_id = excluded.base_parent_id`,
    [normalized, getCwdHashForFileIndex(normalized), now, SYMBOL_INDEX_VERSION, kind, baseParentId],
  );
  const row = db.get('SELECT id FROM symbol_index_parent_path WHERE path = ?', [normalized]) as
    | { id: number | bigint }
    | null;
  if (row == null) throw new Error(`Failed to upsert symbol index parent path: ${normalized}`);
  return Number(row.id);
}

function rowToSymbolIndexParentContext(
  row: { id: number | bigint; path: string; kind: 'base' | 'overlay'; base_parent_id: number | bigint | null },
): SymbolIndexParentRow {
  return {
    parentId: Number(row.id),
    path: row.path,
    kind: row.kind,
    baseParentId: row.base_parent_id != null ? Number(row.base_parent_id) : null,
  };
}

export function getSymbolIndexParentContextById(db: Database, parentId: number): SymbolIndexParentRow | null {
  const row = db.get(
    `SELECT id, path, kind, base_parent_id
     FROM symbol_index_parent_path
     WHERE id = ?`,
    [parentId],
  ) as
    | { id: number | bigint; path: string; kind: 'base' | 'overlay'; base_parent_id: number | bigint | null }
    | null;
  if (row == null) return null;
  return rowToSymbolIndexParentContext(row);
}

export function getSymbolIndexParentContext(db: Database, resolvedParentPath: string): SymbolIndexParentRow | null {
  const row = db.get(
    `SELECT id, path, kind, base_parent_id
     FROM symbol_index_parent_path
     WHERE path = ?`,
    [normalizeParentPath(resolvedParentPath)],
  ) as
    | { id: number | bigint; path: string; kind: 'base' | 'overlay'; base_parent_id: number | bigint | null }
    | null;
  if (row == null) return null;
  return rowToSymbolIndexParentContext(row);
}

export function getSymbolIndexParentId(resolvedParentPath: string): number | null {
  return withCodeNavCacheSqliteSync((db) => {
    const ctx = getSymbolIndexParentContext(db, resolvedParentPath);
    return ctx?.parentId ?? null;
  });
}
